/**
 * Her feeds: what is running, what has gone quiet, and what she has switched off.
 *
 * A regulatory tracker that silently stops fetching is worse than no tracker, because it
 * looks like a quiet fortnight. This is the panel that makes that visible without opening
 * CloudWatch — and, since she owns which feeds she wants, the panel she controls them from.
 *
 * Everything here answers one of two questions and nothing else: **is this feed working**,
 * and **can I have it**. The seven-day strip is the first question answered the only way
 * that really settles it — by what actually arrived, per day, rather than by a status word
 * the system chose for itself. A feed can report "ok" for a fortnight while delivering
 * nothing, and that is exactly the failure a horizon scanner cannot afford.
 *
 * Switching a feed off does not stop it. It keeps fetching and storing; its items arrive
 * held, so they are out of her queue and out of her log but still captured. That is why
 * switching one back on can offer her everything it held in the meantime: there is no gap
 * to apologise for.
 */

import { Fragment, useCallback, useEffect, useState } from "react";

import { api } from "../api";
import { FrameworkVocabulary } from "../components/FrameworkVocabulary";
import { MutedPages } from "../components/MutedPages";
import { SubscriptionApprovals } from "../components/SubscriptionApprovals";
import { formatDateTime } from "../config";
import type { Item, Source, SourceActivity } from "../types";

/** How long a live source may go without a successful run before it looks wrong. */
const QUIET_HOURS = 36;

function hoursSince(iso: string | undefined): number | null {
  if (!iso) return null;
  return (Date.now() - new Date(iso).getTime()) / 3_600_000;
}

function stateOf(source: Source): { label: string; className: string; title?: string } {
  if (source.last_status === "error") {
    return { label: "failing", className: "bad", title: source.last_error };
  }
  const since = hoursSince(source.last_ok_at ?? source.last_run_at);
  if (since === null) return { label: "not run yet", className: "" };
  if (since > QUIET_HOURS) return { label: "quiet", className: "warn" };
  return { label: "ok", className: "" };
}

/** "Mon 11". Short enough for seven columns, unambiguous enough to trust. */
function dayLabel(iso: string): string {
  const date = new Date(`${iso}T12:00:00Z`);
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric" });
}

/**
 * Seven days of arrivals for one feed.
 *
 * Counted by the day we captured an item, not the day the regulator published it. A healthy
 * feed hands us things published last week; a feed that died on Tuesday goes on looking busy
 * for days if you plot it by publication date. Capture date is the only axis that answers
 * "is it still working".
 *
 * Zero is drawn, not omitted. Most feeds are legitimately quiet most days — the FCA can go
 * four days without publishing — so a run of zeroes is information rather than an error, and
 * the number that matters is the total at the end.
 */
function ActivityStrip({
  activity,
  sourceId,
  schedule,
}: {
  activity: SourceActivity;
  sourceId: string;
  schedule: string;
}) {
  const perDay = activity.counts[sourceId] ?? {};
  const total = activity.days.reduce((sum, day) => sum + (perDay[day] ?? 0), 0);
  const busiest = Math.max(1, ...activity.days.map((day) => perDay[day] ?? 0));

  return (
    <div className="activity">
      <div className="activity-days">
        {activity.days.map((day) => {
          const count = perDay[day] ?? 0;
          return (
            <div key={day} className={`activity-day${count ? "" : " empty"}`}>
              <div className="activity-bar-track" title={`${count} on ${day}`}>
                <div
                  className="activity-bar"
                  style={{ height: `${count ? Math.max(8, (count / busiest) * 100) : 0}%` }}
                />
              </div>
              <div className="activity-count">{count}</div>
              <div className="activity-label">{dayLabel(day)}</div>
            </div>
          );
        })}
      </div>
      <p className="small muted activity-total">
        {total === 0 ? (
          <>
            Nothing captured in seven days.{" "}
            {schedule === "none"
              ? "This source runs when something arrives, not on a clock, so a quiet week may be correct."
              : "Quiet weeks are normal for most regulators — check the last run above before worrying."}
          </>
        ) : (
          <>
            <strong>{total}</strong> item{total === 1 ? "" : "s"} captured in seven days.
          </>
        )}
      </p>
    </div>
  );
}

/**
 * What to do with what a feed held while it was off. Deliberately three answers, not two:
 * "leave them" is a real choice, and the default, because a feed she muted for six months
 * may have held a great deal she never wanted to see.
 */
function HeldItems({
  source,
  onReleased,
  onError,
}: {
  source: Source;
  onReleased: () => void;
  onError: (error: unknown) => void;
}) {
  const [open, setOpen] = useState(false);
  const [since, setSince] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);

  const release = (from?: string) => {
    setBusy(true);
    api
      .releaseHeld(source.id, from)
      .then((response) => {
        setResult(
          response.remaining
            ? `${response.released} moved to your queue, ${response.remaining} still held — do it again to continue.`
            : `${response.released} moved to your review queue.`,
        );
        onReleased();
      })
      .catch(onError)
      .finally(() => setBusy(false));
  };

  if (!source.held_count) return null;

  return (
    <div className="held">
      <button className="link" onClick={() => setOpen((was) => !was)}>
        {source.held_count} item{source.held_count === 1 ? "" : "s"} held
      </button>

      {open && (
        <div className="held-choice">
          <p className="small muted">
            Captured while this feed was switched off. They are not in your queue or your
            log. Add them now, or leave them held.
          </p>
          <div className="row">
            <button disabled={busy} onClick={() => release()}>
              Add all {source.held_count}
            </button>
            <label className="inline">
              or only since
              <input
                type="date"
                value={since}
                disabled={busy}
                onChange={(e) => setSince(e.target.value)}
              />
            </label>
            <button disabled={busy || !since} onClick={() => release(since)}>
              Add those
            </button>
            <button className="link" disabled={busy} onClick={() => setOpen(false)}>
              leave them held
            </button>
          </div>
          {result && <p className="small">{result}</p>}
        </div>
      )}
    </div>
  );
}

/**
 * What each schedule group actually means, in her terms rather than the registry's.
 *
 * The registry stores the group a source belongs to — "hourly", "daily", "none" — which says
 * where it runs and not when. These are the when.
 *
 * **This duplicates the cron in `infra/variables.tf`** (`hourly_schedule`, `daily_schedule`),
 * which is the source of truth; the API has no way to read a schedule expression, so changing
 * one means changing both. Kept anyway because "hourly" on its own is the thing that made
 * somebody ask what time it runs.
 */
const CADENCE: Record<string, string> = {
  hourly: "7am to 7pm, Mon-Fri",
  daily: "6:30am, every day",
  none: "when something arrives",
};

export function SourcesView({
  onError,
  onPendingChange,
}: {
  onError: (error: unknown) => void;
  onPendingChange?: (count: number) => void;
}) {
  const [sources, setSources] = useState<Source[]>([]);
  const [activity, setActivity] = useState<SourceActivity | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  /** Pages whose updates she has muted — see `MutedPages`. */
  const [muted, setMuted] = useState<Item[]>([]);

  const load = useCallback(() => {
    Promise.all([
      api.listSources(),
      api.sourceActivity(7),
      // Failing here costs her the muted list, not the sources screen.
      api
        .listChangedItems()
        .catch(() => ({ items: [] as Item[], count: 0, muted: [] as Item[], muted_count: 0 })),
    ])
      .then(([page, counts, changes]) => {
        setSources(page.items);
        setActivity(counts);
        setMuted(changes.muted);
      })
      .catch(onError)
      .finally(() => setLoading(false));
  }, [onError]);

  useEffect(load, [load]);

  /**
   * Switch a muted page back on.
   *
   * The same call the queue's mute makes with the opposite value, so it earns its own audit
   * line. Dropped from the list here rather than reloading the whole screen — the change she
   * will want to see is on the review queue, where it re-appears with whatever the page says
   * now, because muting only ever suppressed the notice and never the record.
   */
  const unmute = async (item: Item) => {
    try {
      await api.patchItem(item.id, { page_change_muted: false });
      setMuted((current) => current.filter((row) => row.id !== item.id));
    } catch (error) {
      onError(error);
    }
  };

  const toggle = (source: Source) => {
    setBusy(source.id);
    api
      .setSourceEnabled(source.id, !source.user_enabled)
      .then(load)
      .catch(onError)
      .finally(() => setBusy(null));
  };

  if (loading) return <div className="centred muted">Loading sources…</div>;

  const running = sources.filter((s) => s.available && s.user_enabled);
  const paused = sources.filter((s) => s.available && !s.user_enabled);
  const unavailable = sources.filter((s) => !s.available);

  /** Seven-day total, used in the table so the strip is not the only place it shows. */
  const weekTotal = (id: string) =>
    activity ? activity.days.reduce((sum, day) => sum + (activity.counts[id]?.[day] ?? 0), 0) : 0;

  const row = (source: Source) => {
    const state = stateOf(source);
    const open = expanded === source.id;
    const week = weekTotal(source.id);
    return (
      <Fragment key={source.id}>
        <tr
          className={`clickable${source.user_enabled ? "" : " muted"} ${
            source.user_enabled ? state.className : ""
          }`}
          onClick={() => setExpanded(open ? null : source.id)}
        >
          <td>
            <span className="disclosure">{open ? "▾" : "▸"}</span>
            <strong>{source.name}</strong>
            <div className="muted small">{source.id}</div>
          </td>
          <td>{source.regulator}</td>
          <td className="muted">
            {source.schedule}
            {CADENCE[source.schedule] && (
              <div className="small">{CADENCE[source.schedule]}</div>
            )}
          </td>
          <td>{formatDateTime(source.last_run_at) || <span className="muted">never</span>}</td>
          <td>{formatDateTime(source.last_item_at) || <span className="muted">—</span>}</td>
          <td className={week ? "" : "muted"}>{week}</td>
          <td>
            {source.user_enabled ? (
              <span title={state.title}>{state.label}</span>
            ) : (
              <span className="pill archived">switched off</span>
            )}
          </td>
          <td>
            <button
              disabled={busy === source.id}
              onClick={(event) => {
                event.stopPropagation();
                toggle(source);
              }}
            >
              {source.user_enabled ? "Switch off" : "Switch on"}
            </button>
          </td>
        </tr>
        {open && (
          <tr className="detail-row">
            <td colSpan={8}>
              {activity && (
                <ActivityStrip
                  activity={activity}
                  sourceId={source.id}
                  schedule={source.schedule}
                />
              )}
              <dl className="source-facts">
                {source.url && (
                  <>
                    <dt>Fetching</dt>
                    <dd>
                      <a href={source.url} target="_blank" rel="noreferrer noopener">
                        {source.url}
                      </a>
                    </dd>
                  </>
                )}
                <dt>Kind</dt>
                <dd>{source.type}</dd>
                {source.last_status === "error" && source.last_error && (
                  <>
                    <dt>Last error</dt>
                    <dd className="bad">{source.last_error}</dd>
                  </>
                )}
                {source.total_new_items !== undefined && (
                  <>
                    <dt>Items all time</dt>
                    <dd>{source.total_new_items}</dd>
                  </>
                )}
                {source.notes && (
                  <>
                    <dt>Notes</dt>
                    <dd className="muted">{source.notes}</dd>
                  </>
                )}
              </dl>
              <HeldItems source={source} onReleased={load} onError={onError} />
            </td>
          </tr>
        )}
      </Fragment>
    );
  };

  const liveCount = running.reduce((sum, s) => sum + weekTotal(s.id), 0);
  const silent = running.filter((s) => weekTotal(s.id) === 0);

  return (
    <div className="sources">
      <SubscriptionApprovals onError={onError} onCountChange={onPendingChange} />

      <div className="toolbar">
        <strong>
          {running.length} feed{running.length === 1 ? "" : "s"} running
        </strong>
        <span className="muted">
          {liveCount} item{liveCount === 1 ? "" : "s"} in seven days
        </span>
        {paused.length > 0 && <span className="muted">{paused.length} switched off</span>}
        <span className="spacer" />
        <span className="muted small">Click a feed for its last seven days.</span>
      </div>

      {silent.length > 0 && (
        <p className="small muted quiet-note">
          Nothing at all in seven days from {silent.map((s) => s.name).join(", ")}. That is
          normal for a feed whose regulator has been quiet — open it and check the last run
          before treating it as broken.
        </p>
      )}

      <table>
        <thead>
          <tr>
            <th>Feed</th>
            <th>Regulator</th>
            <th>Runs</th>
            <th>Last run</th>
            <th>Last new item</th>
            <th>7 days</th>
            <th>State</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {running.map(row)}
          {paused.map(row)}
        </tbody>
      </table>

      {unavailable.length > 0 && (
        <>
          <h3 className="muted">Not being fetched</h3>
          <p className="muted small">
            Sources the system knows about but is not polling. Each says why — most have no
            machine-readable feed at all, which is a fact about the regulator rather than a
            setting. These change in the registry, not here.
          </p>
          <ul className="cards source-cards">
            {unavailable.map((source) => (
              <li key={source.id} className="card">
                <div className="row">
                  <h3>{source.name}</h3>
                  <span className="pill archived">
                    {source.url ? "feed exists, off" : "no feed"}
                  </span>
                  <span className="spacer" />
                  <span className="muted small">{source.regulator}</span>
                </div>
                <div className="muted small">{source.id}</div>
                {source.url && (
                  <a
                    className="small"
                    href={source.url}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {source.url}
                  </a>
                )}
                {source.notes && (
                  <details className="scope-details">
                    <summary>Why it is off</summary>
                    <p className="small muted note-body">{source.notes}</p>
                  </details>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Not a source, but the same kind of thing and the same audience: a vocabulary the
          system reads, that is hers to maintain rather than Craig's to deploy. */}
      <FrameworkVocabulary onError={onError} />

      <MutedPages items={muted} onUnmute={unmute} />
    </div>
  );
}
